import {
  Alert,
  Box, Button, Checkbox, CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel, InputAdornment, MenuItem,
  TextField
} from '@mui/material'
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PointerSvg from '../../../assets/images/Pointer.svg'
import AddressAutocomplete from '../../inputs/AddressAutocomplete'
// @ts-ignore
import { yupResolver } from '@hookform/resolvers/yup'
import PlaceIcon from '@mui/icons-material/Place'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import moment from 'moment-timezone'
import { useSnackbar } from 'notistack'
import * as yup from 'yup'
import useDebounce from '../../../hooks/useDebounce'
import { postLogData } from '../../../services/appApi'
import { createRide, getEstimateRoad } from '../../../services/create-ride/api'
import useAppStore from '../../../stores/app'
import { formatDate, stringifyError } from '../../../utils/utils-functions'
import { yupPhoneNumber } from '../../../utils/yupSchemMethods'
import ConfirmAlert from './ConfirmAlert'
import './createRideDialog.scss'
import LocationMap from './LocationMap'
import { FormData } from './types'

export type ICreateRideProps = {
  onClose: (value: boolean) => void;
  open: boolean;
}

const zipCodeTest = (addressField: string) => yup.string().required('Required').test(addressField, 'Doesn\'t match address', function (value, context) {
  const address = context.parent[addressField]
  return address.zip_code === value
})

const validRiderName = () => yup.string()
  .min(2, 'Must be at least 2 characters')
  .max(30, 'Must be at most 30 characters!')
  .matches(/^[a-zA-Z ()-]+$/, {
    excludeEmptyString: true,
    message: 'Only alphabetical letters and symbols - ( ) are allowed'
  })
  .test('trim', 'No leading or trailing spaces allowed', value => {
    if (!value) return true
    return value === value.trim()
  })

const schema = yup.object().shape({
  phone_number: yupPhoneNumber(),
  mta_client_id: yup.string().matches(/^[0-9]+$/, 'Must be a number').required('Required'),
  mta_ref_id: yup.string().matches(/^[0-9]+$/, 'Must be a number').required('Required'),
  first_name: validRiderName().required('Required'),
  last_name: validRiderName().required('Required'),
  start_address: yup.object().shape({
    address: yup.string().required('Required')
  }),
  start_address_zip_code: zipCodeTest('start_address'),
  end_address: yup.object().shape({
    address: yup.string().required('Required')
  }),
  end_address_zip_code: zipCodeTest('end_address'),
  clients: yup.string().required('Required'),
  companions: yup.string().required('Required'),
  pcas: yup.string().required('Required'),
  note: yup.string().max(250),
  is_service_animal: yup.boolean(),
  pickup_at_date: yup.string().required('Required'),
  pickup_at_time: yup.string().required('Required'),
  fare_type: yup.string().required('Required'),
  equipment_codes: yup.array().of(yup.string()).min(1, 'At least one equipment must be selected').required('Required'),
  estimate_id: yup.string().required('Required'),
  payment_type: yup.string().required('Required')
})
const CreateRideDialog: FC<ICreateRideProps> = ({ onClose, open }) => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { ride_types,
    fare_types,
    payment_types,
    equipment_codes,
    companions,
    clients,
    pcas,
    companion_price
  } = useAppStore()

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      phone_number: '',
      mta_client_id: '',
      mta_ref_id: '',
      first_name: '',
      last_name: '',
      start_address: {
        address: '',
        zip_code: '',
        lat: null,
        lon: null
      },
      start_address_zip_code: '',
      end_address: {
        address: '',
        zip_code: '',
        lat: null,
        lon: null
      },
      end_address_zip_code: '',
      clients: clients[0].toString(),
      companions: companions[0].toString(),
      pcas: pcas[0].toString(),
      note: '',
      is_service_animal: false,
      pickup_at_date: '',
      pickup_at_time: '',
      fare_type: 'regular',
      equipment_codes: [],
      estimate_id: '',
      payment_type: 'cash'
    },
    mode: 'onBlur',
    resolver: yupResolver(schema) as any
  })

  const formFields = watch()
  const pickup_at_date = formFields.pickup_at_date
  const [isValidToSubmit, setIsValidToSubmit] = useState(false)
  const [startAddressLogData, setStartAddressLogData] = useState(null)
  const [endAddressLogData, setEndAddressLogData] = useState(null)
  const debouncedPickupDate = useDebounce(formFields.pickup_at_date, 1000)
  const debouncedPickupTime = useDebounce(formFields.pickup_at_time, 1000)

  const paymentTypes = [
    { label: 'Cash', value: 'cash' },
    { label: 'OMNY', value: 'omny' }
  ]

  useEffect(() => {
    const subscription = watch(() => {
      setIsValidToSubmit(false)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const { mutateAsync: postLogDataRequest } = useMutation(postLogData)
  const { mutateAsync: createRideRequest, isLoading: createRideIsLoading } = useMutation(createRide, {
    onSuccess: () => queryClient.refetchQueries({
      queryKey: ['rides']
    })
  })

  const { data: estimateRoad,
    isLoading: isLoadingEstimateRoad,
    isStale: isStaleEstimateRoad,
    refetch: refetchEstimateRoad
  } = useQuery({
    staleTime: 110000,
    queryKey: ['estimateRoad', formFields.start_address.address, formFields.end_address.address, debouncedPickupDate, debouncedPickupTime],
    enabled: Boolean(formFields.start_address.address && formFields.end_address.address && debouncedPickupDate && debouncedPickupTime),
    queryFn: () => {
      if (formFields.start_address.lat === null || formFields.end_address.lat === null || formFields.start_address.lon === null || formFields.end_address.lon === null) {
        return null
      }

      const pickupMoment = moment(`${debouncedPickupDate} ${debouncedPickupTime}`, 'YYYY-MM-DD HH:mm:ss')
      if (!pickupMoment.isValid()) {
        return null
      }
      const pickup_at = pickupMoment.format('YYYY-MM-DD HH:mm:ss')

      return getEstimateRoad({
        start_address: formFields.start_address.address,
        start_lat: formFields.start_address.lat,
        start_lon: formFields.start_address.lon,
        end_address: formFields.end_address.address,
        end_lat: formFields.end_address.lat,
        end_lon: formFields.end_address.lon,
        pickup_at
      })
    }
  })

  useEffect(() => {
    if (isStaleEstimateRoad && !document.hidden) {
      refetchEstimateRoad()
    }
  }, [isStaleEstimateRoad])

  const handlerCreateRide = async (data: FormData) => {
    try {
      if (!isValidToSubmit) {
        setIsValidToSubmit(true)
        return
      }
      const rideData = {
        ...data,
        pickup_note: data.note,
        pickup_at_dates: formatDate(data.pickup_at_date),
        ride_type_id: Number(data.estimate_id),
        start_address: formFields.start_address.address,
        start_lat: formFields.start_address.lat,
        start_lon: formFields.start_address.lon,
        end_address: formFields.end_address.address,
        end_lat: formFields.end_address.lat,
        end_lon: formFields.end_address.lon,
        clients: Number(formFields.clients),
        companions: Number(formFields.companions),
        pcas: Number(formFields.pcas)
      }
      const ride = await createRideRequest(rideData)
      enqueueSnackbar('Ride created', { variant: 'success' })
      postLogDataRequest({
        startAddressLogData,
        endAddressLogData,
        rideId: ride.rides[0].ride.id
      })

      handlerClose()
    } catch (e) {
      console.error(e)
      enqueueSnackbar(stringifyError(e), { variant: 'error' })
    }
  }

  const aarFarePrice = useMemo(() => {
    const selectedFareType = fare_types?.find(type => type.value === formFields.fare_type)
    const fare = selectedFareType?.fare || 0
    const clientCash = fare * Number(formFields.clients)

    if (formFields.fare_type === 'free_fare') {
      return 0
    } else if (formFields.payment_type === 'omny') {
      return Number(formFields.companions) * companion_price
    } else {
      return clientCash + Number(formFields.companions) * companion_price
    }
  }, [
    formFields.clients,
    formFields.fare_type,
    fare_types,
    formFields.payment_type,
    payment_types,
    companion_price,
    formFields.companions
  ])
  const handlerChangeEquipment = (onChangeInput: (e :ChangeEvent<HTMLInputElement>) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    onChangeInput(event)
    const value = event.target.value as unknown as string[]
    if (value.some(equipment => ['lr', 'wc', 'wd'].includes(equipment))) {
      setValue('estimate_id', '123', { shouldValidate: true })
    }
  }

  const isRideDateInNotToday = useMemo(() => {
    const isValidDate = pickup_at_date && moment(pickup_at_date).isValid()
    return isValidDate ? !moment(pickup_at_date).isSame(moment(), 'day') : false
  }, [pickup_at_date])

  const handlerClose = () => {
    onClose(false)
  }

  return (
    <Dialog
      className="create-ride-dialog"
      PaperProps={{
        style: {
          maxWidth: '1400px'
        }
      }}
      fullWidth
      open={open}
      onClose={handlerClose}
    >
      <DialogTitle>
        Create Ride
      </DialogTitle>
      <DialogContent>
        <form
          id="create-ride-form"
          noValidate
          autoComplete="off"
          className="create-ride-form"
          onSubmit={handleSubmit(handlerCreateRide)}>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="phone_number"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  error={Boolean(errors.phone_number)}
                  helperText={errors.phone_number?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}/>
              )}/>
            <Controller
              name="mta_client_id"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Client ID"
                  error={Boolean(errors.mta_client_id)}
                  helperText={errors.mta_client_id?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}/>
              )}/>
            <Controller
              name="mta_ref_id"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Trip ID"
                  error={Boolean(errors.mta_ref_id)}
                  helperText={errors.mta_ref_id?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}/>
              )}/>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="first_name"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="First Name"
                  error={Boolean(errors.first_name)}
                  helperText={errors.first_name?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}/>
              )}/>
            <Controller
              name="last_name"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Last Name"
                  error={Boolean(errors.last_name)}
                  helperText={errors.last_name?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}/>
              )}/>
          </Box>
          <Controller
            name="is_service_animal"
            control={control}
            render={({ field: { value, onChange, ref } }) => (
              <FormControlLabel
                value="end"
                control={<Checkbox
                  size="small"
                  checked={value}
                  inputRef={ref}
                  onChange={onChange}/>}
                label="Service Animal"
                labelPlacement="end"/>)}/>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="start_address"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AddressAutocomplete
                  onChange={options => {
                    onChange(options)
                    trigger('start_address_zip_code')
                  }}
                  name="start_address"
                  value={value.address}
                  setLogData={setStartAddressLogData}
                  renderOutput={(
                    <TextField
                      fullWidth
                      size="small"
                      error={Boolean(errors.start_address?.address)}
                      helperText={errors.start_address?.address?.message}
                      label="Start Address"
                      className="start-address-text-field"
                      value={value.address}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <PlaceIcon color="info"/>
                      }}
                      variant="outlined"/>
                  )}/>
              )}/>
            <Controller
              name="start_address_zip_code"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  sx={{
                    minWidth: '160px'
                  }}
                  required
                  size="small"
                  label="Zip Code"
                  error={Boolean(errors.start_address_zip_code)}
                  helperText={errors.start_address_zip_code?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}/>
              )}/>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="end_address"
              control={control}
              render={({ field: { value, onChange } }) => (
                <AddressAutocomplete
                  onChange={options => {
                    onChange(options)
                    trigger('end_address_zip_code')
                  }}
                  name="end_address"
                  value={value.address}
                  setLogData={setEndAddressLogData}
                  renderOutput={(
                    <TextField
                      fullWidth
                      size="small"
                      error={Boolean(errors.end_address?.address)}
                      helperText={errors.end_address?.address?.message}
                      label="End Address"
                      className="end-address-text-field"
                      value={value.address}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <PlaceIcon color="success"/>
                      }}
                      variant="outlined"/>
                  )}/>
              )}/>
            <Controller
              name="end_address_zip_code"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  sx={{
                    minWidth: '160px'
                  }}
                  required
                  size="small"
                  label="Zip Code"
                  error={Boolean(errors.end_address_zip_code)}
                  helperText={errors.end_address_zip_code?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}/>
              )}/>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="fare_type"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  required
                  fullWidth
                  size="small"
                  label="Fare Type"
                  error={Boolean(errors.fare_type)}
                  helperText={errors.fare_type?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}>
                  {fare_types
                    ?.filter(({ value }) => value !== 'OMNY')
                    .map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                </TextField>
              )}/>
            <Controller
              name="clients"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  label="Clients"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}>
                  ({clients?.map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              )}/>
            <Controller
              name="companions"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  label="Companions"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}>
                  {companions?.map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              )}/>
            <Controller
              name="pcas"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  label="PCAs"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}>
                  ({pcas?.map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              )}/>
            <Controller
              name="payment_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Payment Type"
                  required
                  fullWidth
                  size="small"
                >
                  {paymentTypes.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField
              fullWidth
              size="small"
              label="AAR Fare"
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              value={aarFarePrice.toFixed(2)}/>
          </Box>
          <Controller
            name="note"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextField
                fullWidth
                size="small"
                label="Pickup Note"
                placeholder="Do not include any personal information"
                error={Boolean(errors.note)}
                helperText={errors.note?.message}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                multiline
                rows={4}/>
            )}/>
          <div className="distance">
            {estimateRoad ? (
              <>
                <img className="distance-image" alt="" src={PointerSvg}/>
                {isLoadingEstimateRoad ? <CircularProgress className="circular-progress"/> : (
                  <div>
                    <h3>{estimateRoad.distance.toFixed(2)} Mile</h3>
                    <h5>{moment.duration(estimateRoad.duration, 'seconds').asMinutes().toFixed()} min ride</h5>
                  </div>
                )}
              </>
            ) : (
              <h3>
                Please enter a valid start and end address, date and time to get an estimate.
              </h3>
            )}
          </div>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="pickup_at_date"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Ride Date"
                  className={clsx({ 'ride-time-warning': isRideDateInNotToday })}
                  error={Boolean(errors.pickup_at_date)}
                  helperText={errors.pickup_at_date?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="date"
                  InputLabelProps={{ shrink: true }}/>
              )}/>
            <Controller
              name="pickup_at_time"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Ride Time"
                  error={Boolean(errors.pickup_at_time)}
                  helperText={errors.pickup_at_time?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="time"
                  InputLabelProps={{ shrink: true }}/>
              )}/>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '20px'
            }}
          >
            <Controller
              name="equipment_codes"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  required
                  fullWidth
                  size="small"
                  label="Equipment"
                  error={Boolean(errors.equipment_codes)}
                  helperText={errors.equipment_codes?.message}
                  value={value}
                  onChange={handlerChangeEquipment(onChange)}
                  onBlur={onBlur}
                  SelectProps={{ multiple: true }}>
                  {equipment_codes?.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}/>
            <Controller
              name="estimate_id"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  select
                  required
                  fullWidth
                  size="small"
                  disabled={formFields.equipment_codes.some(equipment => ['lr', 'wc', 'wd'].includes(equipment))}
                  label="Ride Type"
                  error={Boolean(errors.estimate_id)}
                  helperText={errors.estimate_id?.message}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}>
                  {ride_types?.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}/>
          </Box>
        </form>
        <LocationMap
          pickupAddress={formFields.start_address}
          dropoffAddress={formFields.end_address}/>
        {isRideDateInNotToday ? (
          <Alert variant="filled" severity="warning" className="ride-time-warning">
            The selected day is not today.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        {isValidToSubmit ? (
          <ConfirmAlert
            form="create-ride-form"
            loading={createRideIsLoading}
          />
        ) : (
          <Button
            form="create-ride-form"
            variant="contained"
            type="submit">
            Create Ride
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateRideDialog
